import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize, interval, map, takeWhile, tap } from 'rxjs';
import { fadeAnimation } from '../../animations/route-animations';
import { injectScreenIndex } from '../../flow/flow-routing';
import { AnalyticsService } from '../../../../../shared/src/services/analytics.service';
import { FunnelFlowService } from '../../services/funnel-flow.service';

@Component({
  selector: 'funnel-customization',
  imports: [MatProgressSpinnerModule],
  animations: [fadeAnimation],
  templateUrl: './customization.component.html',
  styleUrl: './customization.component.css',
})
export class CustomizationComponent implements OnInit {
  value = 0;

  private readonly destroyRef = inject(DestroyRef);
  private readonly screenIndex = injectScreenIndex();
  private duration = Math.random() * 5000 + 6000;
  private intervalDuration = 50;

  constructor(
    private flowService: FunnelFlowService,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.analyticsService.track('web_customization_started');

    this.createProgressObservable(0, 100, this.duration)
      .pipe(
        finalize(() => {
          if (this.value >= 100) {
            this.analyticsService.track('web_customization_completed');
            this.flowService.handleFinishedScreen(this.screenIndex);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private createProgressObservable(startValue: number, endValue: number, duration: number) {
    const progressDifference = endValue - startValue;

    return interval(this.intervalDuration).pipe(
      map(elapsedIntervals => {
        const elapsed = (elapsedIntervals + 1) * this.intervalDuration;
        const t = Math.min(elapsed / duration, 1);
        const easedProgress = this.cubicEaseOut(t);
        const currentValue = Math.min(
          Math.round(startValue + easedProgress * progressDifference),
          endValue
        );
        return currentValue;
      }),
      tap((currentValue: number) => {
        this.value = currentValue;
      }),
      takeWhile((currentValue: number) => currentValue < endValue, true)
    );
  }

  private cubicEaseOut(t: number): number {
    return --t * t * t + 1;
  }
}
